import TR from 'utils/TR';
import { Chart, layouts } from 'chart.js';
import Section from 'components/common/Section';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';
import useSetChartData from 'hooks/app/useSetChartData';

const columns = Array.from({ length: 25 }).map((_, index) => index);
const config = (hash, onComplete, duration) => ({
  data: {
    labels: columns,
    datasets: [
      {
        barThickness: 12,
        data: hash.bolus,
        borderRadius: [{
          topLeft: 6,
          topRight: 6,
        }],
        borderSkipped: false,
        type: 'bar',
        order: 1,
      }, {
        data: hash.base,
        pointRadius: 4,
        pointBackgroundColor: '#ffffff',
        pointBorderWidth: 3,
        borderColor: '#5C69FF',
        backgroundColor: '#5C69FF',
        type: 'line',
        order: 0,
      }
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        left: 20,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        border: {
          dash: [4, 4],
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          display: true,
          lineWidth: (context) => {
            switch (columns[context.index]) {
              case 6:
              case 7:
              case 12:
              case 13:
              case 18:
              case 19:
                return 1;
              default: return 0;
            }
          },
        },
        ticks: {
          callback: (value) => {
            if (value % 6 === 0) {
              return value + 'H';
            } else {
              return '';
            }
          },
          color: '#aaaaaa',
          stepSize: 6,
        }
      },
      y: {
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: 1,
          display: true,
        },
        min: 0,
        max: Math.ceil(hash.max / 40) * 40,
        ticks: {
          color: '#aaaaaa',
          stepSize: 20,
          // display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  },
});

export default function InsulinPatternChart(props) {
  const {
    insulin_pattern_time_graph,
    duration,
    chartData,
    ...rest
  } = props;


  const setChartData = useSetChartData('InsulinStatisticsChart');
  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (chartData.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [chartData.uninitialized, duration, setChartData]);

  const hash = React.useMemo(() => {
    const data = {
      bolus: [],
      base: [],
      max: 0,
    };
    insulin_pattern_time_graph.forEach((item) => {
      const {
        time,
        bolus_amt,
        giju_amt,
      } = item;
      data.bolus.push(bolus_amt);
      data.base.push(giju_amt);
      if (data.max < bolus_amt) data.max = bolus_amt;
      if (data.max < giju_amt) data.max = giju_amt;
    });
    return data;
  }, [insulin_pattern_time_graph]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const ctx = canvasRef.current.getContext('2d', { willReadFrequently: !duration });
    const backgroundColor = ctx.createLinearGradient(0, 0, 0, 350);
    backgroundColor.addColorStop(0, '#CA99FF');
    backgroundColor.addColorStop(1, '#4CA9FF');
    const settings = config(hash, onComplete, duration);
    settings.data.datasets[0].backgroundColor = backgroundColor;
    const chart = new Chart(canvasRef.current, settings);
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [duration, hash, onComplete]);
  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);

  return (
    <div
      {...ex(['Chart', 'InsulinPatternChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}
